// src/XPContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import {
  XP_PER_HABIT,
  getLevelFromTotalXP,
} from "./xpConfig";

const XPContext = createContext();
export const useXP = () => {
  const context = useContext(XPContext);
  if (!context) {
    throw new Error("useXP must be used within an XPProvider");
  }
  return context;
};

export const XPProvider = ({ children }) => {
  const { user } = useAuth();
  const [totalXP, setTotalXP] = useState(0);
  const [levelUpTrigger, setLevelUpTrigger] = useState(0);
  const [isAwarding, setIsAwarding] = useState(false);

  const progression = getLevelFromTotalXP(totalXP);

  const fetchProgression = useCallback(async () => {
    if (!user) {
      setTotalXP(0);
      return;
    }

    try {
      const { data } = await supabase
        .from("user_profiles")
        .select("total_xp")
        .eq("id", user.id)
        .single();

      if (data && data.total_xp != null) {
        setTotalXP(data.total_xp);
      } else {
        setTotalXP(0);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching XP:", err);
      setTotalXP(0);
    }
  }, [user]);

  useEffect(() => {
    fetchProgression();
  }, [fetchProgression]);

  const awardXPForHabit = useCallback(
    async (dateKey, habitName) => {
      if (!user || isAwarding) return { awarded: false, leveledUp: false };

      try {
        setIsAwarding(true);

        const { data, error } = await supabase.rpc("award_xp_for_habit", {
          p_date_key: dateKey,
          p_habit_name: habitName,
          p_xp_amount: XP_PER_HABIT,
        });

        if (error || !data?.awarded) {
          return { awarded: false, leveledUp: false };
        }

        setTotalXP(data.total_xp);
        if (data.leveled_up) {
          setLevelUpTrigger((t) => t + 1);
        }

        return { awarded: true, leveledUp: data.leveled_up };
      } catch (err) {
        return { awarded: false, leveledUp: false };
      } finally {
        setIsAwarding(false);
      }
    },
    [user, isAwarding]
  );

  const revokeXPForHabit = useCallback(
    async (dateKey, habitName) => {
      if (!user) return { revoked: false };

      try {
        const { data, error } = await supabase.rpc("revoke_xp_for_habit", {
          p_date_key: dateKey,
          p_habit_name: habitName,
        });

        if (error || !data?.revoked) {
          return { revoked: false };
        }

        setTotalXP(data.total_xp);
        return { revoked: true };
      } catch (err) {
        return { revoked: false };
      }
    },
    [user]
  );

  const value = {
    totalXP,
    progression,
    fetchProgression,
    awardXPForHabit,
    revokeXPForHabit,
    levelUpTrigger,
  };

  return <XPContext.Provider value={value}>{children}</XPContext.Provider>;
};
